import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subDays,
  subMonths,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  isAfter,
} from "date-fns";
import { ptBR } from "date-fns/locale";


const DIAS = ["D", "S", "T", "Q", "Q", "S", "S"];

const useStyles = makeStyles((theme) => ({
  botao: {
    justifyContent: "flex-start",
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    padding: "8px 12px",
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    "&:hover": { borderColor: theme.palette.primary.main },
  },
  iconeBotao: {
    fontSize: 15,
    marginRight: 8,
    color: theme.palette.primary.main,
  },
  painel: {
    display: "flex",
    borderRadius: 12,
  },
  atalhos: {
    display: "flex",
    flexDirection: "column",
    padding: "8px 0",
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: 150,
  },
  atalho: {
    justifyContent: "flex-start",
    textTransform: "none",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 0,
    padding: "7px 18px",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(30,152,85,0.16)"
        : "rgba(30,152,85,0.08)",
      color: theme.palette.primary.main,
    },
  },
  calendario: {
    padding: "10px 14px 14px",
  },
  cabecalho: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  mesAno: {
    fontSize: 13,
    fontWeight: 700,
    textTransform: "capitalize",
    color: theme.palette.text.primary,
  },
  grade: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 32px)",
    gap: 2,
  },
  nomeDia: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textAlign: "center",
    lineHeight: "24px",
  },
  dia: {
    height: 30,
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: "inherit",
    backgroundColor: "transparent",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(30,152,85,0.20)"
        : "rgba(30,152,85,0.12)",
    },
  },
  foraDoMes: {
    color: theme.palette.text.secondary,
    opacity: 0.4,
  },
  hoje: {
    boxShadow: `inset 0 0 0 1.5px ${theme.palette.primary.main}`,
  },
  
  noIntervalo: {
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(30,152,85,0.22)"
      : "rgba(30,152,85,0.14)",
    borderRadius: 0,
  },
  ponta: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: "#fff !important",
  },
  rodape: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 8,
    paddingTop: 8,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const iso = (d) => format(d, "yyyy-MM-dd");

const FiltroData = ({ value, onChange }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mesVisivel, setMesVisivel] = useState(new Date());

  const de = value?.from ? parseISO(value.from) : null;
  const ate = value?.to ? parseISO(value.to) : null;

  const rotulo = useMemo(() => {
    if (!de) return "Todas as datas";
    if (!ate || isSameDay(de, ate)) return format(de, "dd/MM/yyyy");
    return `${format(de, "dd/MM")} – ${format(ate, "dd/MM/yyyy")}`;
  }, [de, ate]);

  const aplicar = (inicio, fim) => {
    onChange({ from: inicio ? iso(inicio) : "", to: fim ? iso(fim) : "" });
  };

  const atalhos = [
    { nome: "Hoje", calc: () => [new Date(), new Date()] },
    { nome: "Ontem", calc: () => [subDays(new Date(), 1), subDays(new Date(), 1)] },
    { nome: "Últimos 7 dias", calc: () => [subDays(new Date(), 6), new Date()] },
    { nome: "Últimos 30 dias", calc: () => [subDays(new Date(), 29), new Date()] },
    { nome: "Este mês", calc: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
    {
      nome: "Mês passado",
      calc: () => {
        const m = subMonths(new Date(), 1);
        return [startOfMonth(m), endOfMonth(m)];
      },
    },
    { nome: "Tudo", calc: () => [null, null] },
  ];

  const semanas = useMemo(() => {
    const inicio = startOfWeek(startOfMonth(mesVisivel), { locale: ptBR });
    const fim = endOfWeek(endOfMonth(mesVisivel), { locale: ptBR });
    const dias = [];
    let d = inicio;
    while (d <= fim) {
      dias.push(d);
      d = addDays(d, 1);
    }
    return dias;
  }, [mesVisivel]);

  const clicarDia = (dia) => {
    
    if (!de || (de && ate)) {
      aplicar(dia, dia);
      return;
    }
    if (isAfter(dia, de)) aplicar(de, dia);
    else aplicar(dia, de);
  };

  const classeDoDia = (dia) => {
    const cls = [classes.dia];
    if (!isSameMonth(dia, mesVisivel)) cls.push(classes.foraDoMes);
    if (isSameDay(dia, new Date())) cls.push(classes.hoje);

    if (de && ate && isWithinInterval(dia, { start: de, end: ate })) {
      cls.push(classes.noIntervalo);
    }
    if ((de && isSameDay(dia, de)) || (ate && isSameDay(dia, ate))) {
      cls.push(classes.ponta);
    }
    return cls.join(" ");
  };

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        className={classes.botao}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <CalendarTodayIcon className={classes.iconeBotao} />
        {rotulo}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: classes.painel }}
      >
        <div className={classes.atalhos}>
          {atalhos.map((a) => (
            <Button
              key={a.nome}
              className={classes.atalho}
              onClick={() => {
                const [i, f] = a.calc();
                aplicar(i, f);
                if (i) setMesVisivel(i);
                setAnchorEl(null);
              }}
            >
              {a.nome}
            </Button>
          ))}
        </div>

        <div className={classes.calendario}>
          <div className={classes.cabecalho}>
            <IconButton size="small" onClick={() => setMesVisivel(subMonths(mesVisivel, 1))}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <span className={classes.mesAno}>
              {format(mesVisivel, "MMMM yyyy", { locale: ptBR })}
            </span>
            <IconButton size="small" onClick={() => setMesVisivel(addMonths(mesVisivel, 1))}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.grade}>
            {DIAS.map((d, i) => (
              <div key={`${d}-${i}`} className={classes.nomeDia}>
                {d}
              </div>
            ))}
            {semanas.map((dia) => (
              <button
                type="button"
                key={dia.toISOString()}
                className={classeDoDia(dia)}
                onClick={() => clicarDia(dia)}
              >
                {format(dia, "d")}
              </button>
            ))}
          </div>

          <div className={classes.rodape}>
            <Button size="small" onClick={() => aplicar(null, null)}>
              Limpar
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={() => {
                aplicar(new Date(), new Date());
                setMesVisivel(new Date());
              }}
            >
              Hoje
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default FiltroData;
